import { Controller } from "@hotwired/stimulus"
import { filterAndSortRoles, escapeHtml } from "../utils/role_search"
import { bindClickOutside } from "../utils/click_outside"
import { handleListKeydown } from "../utils/keyboard_nav"

// Job Role Autocomplete Controller
// Provides autocomplete search for job role variants with smart ordering
export default class extends Controller {
  static targets = ["input", "results", "hiddenField", "badge", "variantsData"]
  static values = {
    selected: Object
  }

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.filteredVariants = []
    this.variants = []

    // Load variants from script tag
    if (this.hasVariantsDataTarget) {
      try {
        this.variants = JSON.parse(this.variantsDataTarget.textContent)
      } catch (e) {
        console.error("Failed to parse variants JSON:", e)
      }
    }

    // Close dropdown when clicking outside
    this.unbindClickOutside = bindClickOutside(this.element, () => this.closeResults())
  }

  disconnect() {
    this.unbindClickOutside()
  }

  search(event) {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.closeResults()
      return
    }

    this.filteredVariants = filterAndSortRoles(this.variants, query)
    this.renderResults()
    this.openResults()
  }

  renderResults() {
    if (this.filteredVariants.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-gray-500 text-sm">
          No matching roles found
        </div>
      `
      return
    }

    const html = this.filteredVariants.map((variant, index) => {
      const isHighlighted = index === this.highlightedIndex
      const badge = variant.employment_type === "contract"
        ? `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 ml-2">Contract</span>`
        : ""

      return `
        <div class="px-4 py-2 cursor-pointer ${isHighlighted ? 'bg-gray-100' : 'hover:bg-gray-50'}"
             data-action="click->job-role-autocomplete#select"
             data-index="${index}">
          <div class="flex justify-between items-center">
            <div>
              <div class="font-medium text-sm text-gray-900">${escapeHtml(variant.canonical_title)}${badge}</div>
              <div class="text-gray-500 text-xs">${escapeHtml(variant.career_family || '')}</div>
            </div>
          </div>
        </div>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
  }

  openResults() {
    this.resultsTarget.classList.remove("hidden")
    this.isOpen = true
    this.highlightedIndex = -1
  }

  closeResults() {
    this.resultsTarget.classList.add("hidden")
    this.isOpen = false
    this.highlightedIndex = -1
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const variant = this.filteredVariants[index]

    if (variant) {
      this.selectVariant(variant)
    }
  }

  selectVariant(variant) {
    this.inputTarget.value = variant.canonical_title
    this.hiddenFieldTarget.value = variant.id
    this.selectedValue = variant

    // Show employment type badge if contract
    if (this.hasBadgeTarget) {
      if (variant.employment_type === "contract") {
        this.badgeTarget.textContent = "Contract"
        this.badgeTarget.classList.remove("hidden")
      } else {
        this.badgeTarget.classList.add("hidden")
      }
    }

    this.closeResults()

    // Dispatch custom event with employment type for form controller
    this.element.dispatchEvent(new CustomEvent("role:selected", {
      bubbles: true,
      detail: {
        variantId: variant.id,
        employmentType: variant.employment_type,
        canonicalTitle: variant.canonical_title
      }
    }))
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenFieldTarget.value = ""
    this.selectedValue = {}

    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add("hidden")
    }

    this.closeResults()

    // Dispatch cleared event
    this.element.dispatchEvent(new CustomEvent("role:cleared", {
      bubbles: true
    }))
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    handleListKeydown(event, {
      highlightedIndex: this.highlightedIndex,
      itemCount: this.filteredVariants.length,
      onHighlight: (index) => { this.highlightedIndex = index; this.renderResults() },
      onSelect: (index) => this.selectVariant(this.filteredVariants[index]),
      onClose: () => this.closeResults()
    })
  }
}
